import { Component, inject, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpContext } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AddVitalsComponent } from '../patient-observation/add-vitals/add-vitals.component';
import { AddObservationComponent } from '../patient-observation/add-observation/add-observation.component';
import { PatSympComponent } from '../pat-symp/pat-symp.component';
import { PatientObservationComponent } from '../patient-observation/patient-observation.component';
import { FinalizeTriageDialogComponent } from './finalize-triage-dialog/finalize-triage-dialog.component';
import { UnifiedNotificationService } from '../shared/unified-notification.service';
import { NotificationService } from '../services/notification.service';
import { backendEndPointToken } from '../app.config';
import { ErrorService } from '../shared/error.service';
import { SuccessToastService } from '../shared/success-toast.service';
import { AuthService } from '../shared/auth/auth.service';
import { PatientFilterComponent } from '../shared/patient-filter/patient-filter.component';
import { StateService } from '../shared/state.service';
import { FhirResourceService } from '../shared/fhir-resource.service';
import { combineLatest, expand, EMPTY, of } from 'rxjs';
import { map, reduce, catchError } from 'rxjs/operators';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
import { Bundle, Appointment } from 'fhir/r4';
import { UtilityService } from '../shared/utility.service';
import { LoadingUIEnabled } from '../loading.interceptor';

@Component({
  selector: 'app-triage',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatDividerModule,
    MatDialogModule,
    FormsModule,
    AddVitalsComponent,
    AddObservationComponent,
    PatSympComponent,
    PatientObservationComponent,
    PatientFilterComponent,
    FinalizeTriageDialogComponent
  ],
  templateUrl: './triage.component.html',
  styleUrls: ['./triage.component.scss']
})
export class TriageComponent implements OnInit, OnDestroy {
  patientId: string = '';
  encounterId: string = '';

  route = inject(ActivatedRoute);
  router = inject(Router);
  http = inject(HttpClient);
  notificationService = inject(NotificationService);
  backendUrl = inject(backendEndPointToken);
  errorService = inject(ErrorService);
  successToast = inject(SuccessToastService);
  authService = inject(AuthService);
  stateService = inject(StateService);
  fhirResourceService = inject(FhirResourceService);
  dialog = inject(MatDialog);
  encounterService = inject(EncounterServiceService);
  utilityService = inject(UtilityService);
  unifiedNotificationService = inject(UnifiedNotificationService);

  @ViewChild('highRiskDialogTemplate') highRiskDialogTemplate: any;

  allowedTabs = ['vital-signs', 'exam', 'patient-symptoms'];

  highRiskIndicators = [
    { label: 'Severe chest pain', code: 'severe-chest-pain', checked: false },
    { label: 'Altered mental status', code: 'altered-mental-status', checked: false },
    { label: 'Active bleeding', code: 'active-bleeding', checked: false },
    { label: 'Severe shortness of breath', code: 'severe-shortness-of-breath', checked: false },
    { label: 'Hypoxia (SpO₂ < 92%)', code: 'hypoxia', checked: false },
    { label: 'Syncope with unstable vitals', code: 'syncope-unstable-vitals', checked: false },
    { label: 'Stroke symptoms (weakness, paralysis, speech disturbance)', code: 'stroke-symptoms', checked: false }
  ];

  isSaving = false;
  isInitializingEncounter = false;

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.patientId = params['patientId'] || '';
      
      if (this.patientId) {
        this.loadPatientContext();
        this.checkAndCreateEncounter();
      } else {
        this.encounterId = '';
      }
    });
  }

  ngOnDestroy() {
    // Clean up patient context when leaving Triage
    this.stateService.clearCurrentPatientContext();
  }

  loadPatientContext() {
    this.stateService.currentPatientId$.next(this.patientId);
    this.http.get<any>(`${this.backendUrl}/Patient/${this.patientId}`).subscribe({
      next: (patient) => {
        this.stateService.PatientResources.currentPatient.next({
          referenceId: patient.id ? `Patient/${patient.id}` : null,
          savedStatus: 'saved',
          actualResource: patient
        });
      },
      error: (err) => console.error('Failed to load patient context', err)
    });
  }

  checkAndCreateEncounter() {
    this.isInitializingEncounter = true;
    
    // Query the server for an active encounter for this patient
    this.http.get<any>(`${this.backendUrl}/Encounter?subject=Patient/${this.patientId}&status=in-progress`).subscribe({
      next: (bundle) => {
        const activeEncounter = bundle.entry && bundle.entry.length > 0 ? bundle.entry[0].resource : null;
        
        if (activeEncounter) {
          this.encounterId = activeEncounter.id || '';
          this.stateService.setCurrentEncounter({
            ...activeEncounter,
            patientId: this.patientId
          });
          this.encounterService.setEncounterState(this.patientId, 'in-progress');
          this.isInitializingEncounter = false;
        } else {
          this.createAutomaticEncounter();
        }
      },
      error: (err) => {
        console.error('Failed to fetch encounter', err);
        this.createAutomaticEncounter(); // Fallback to creation on error
      }
    });
  }

  createAutomaticEncounter() {
    const authUser = this.authService.user.getValue();
    const practitionerId = authUser?.['fhirResourceId'];

    const participants = [];
    if (practitionerId) {
      participants.push({ individual: { reference: `Practitioner/${practitionerId}` } });
    }

    const newEncounter: any = {
      resourceType: 'Encounter',
      status: 'in-progress',
      subject: { reference: `Patient/${this.patientId}` },
      period: { start: new Date().toISOString() },
      class: { system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'AMB', display: 'ambulatory' },
      priority: { text: 'Routine' }
    };

    if (participants.length > 0) {
      newEncounter.participant = participants;
    }

    this.http.post<any>(`${this.backendUrl}/Encounter`, newEncounter, {
      headers: {
        'Content-Type': 'application/fhir+json',
        'Accept': 'application/fhir+json'
      }
    }).subscribe({
      next: (createdEncounter: any) => {
        this.encounterId = createdEncounter.id || '';
        this.isInitializingEncounter = false;
        
        // Update StateService to make it available to PatientObservationComponent
        this.stateService.setCurrentEncounter({
          ...createdEncounter,
          patientId: this.patientId
        });
        this.encounterService.setEncounterState(this.patientId, 'in-progress');
      },
      error: (err) => {
        console.error('Failed to create automatic encounter', err);
        this.errorService.openandCloseError('Failed to initialize a new encounter for this patient.');
        this.isInitializingEncounter = false;
      }
    });
  }

  // --- Observation Dialog Triggers --- //

  addVitals() {
    this.dialog.open(AddVitalsComponent, {
      maxHeight: "90vh",
      maxWidth: "680px"
    });
  }

  addPhysicalExam() {
    this.dialog.open(AddObservationComponent, {
      maxHeight: '90vh',
      maxWidth: '1200px',
      autoFocus: false,
      data: {
        isAnyCategory: false,
        observationCategoryValue: 'exam'
      }
    });
  }

  addSymptoms() {
    this.dialog.open(PatSympComponent, {
      maxHeight: '90vh',
      maxWidth: '650px',
      autoFocus: false,
    });
  }

  // --- High Risk Dialog & Logic --- //

  openHighRiskDialog() {
    this.dialog.open(this.highRiskDialogTemplate, {
      width: '450px',
      disableClose: false
    });
  }

  async saveHighRiskIndicators() {
    const checkedIndicators = this.highRiskIndicators.filter(i => i.checked);
    if (checkedIndicators.length === 0) {
      this.dialog.closeAll();
      return;
    }

    this.isSaving = true;
    try {
      const obsPromises = checkedIndicators.map(indicator => {
        const obs = {
          resourceType: "Observation",
          status: "final",
          category: [
            {
              coding: [
                {
                  system: "http://terminology.hl7.org/CodeSystem/observation-category",
                  code: "exam",
                  display: "Exam"
                },
                {
                  system: "http://terminology.hl7.org/CodeSystem/observation-category",
                  code: "physical-exam",
                  display: "Physical Exam"
                }
              ]
            }
          ],
          code: {
            text: indicator.label
          },
          subject: { reference: `Patient/${this.patientId}` },
          encounter: this.encounterId ? { reference: `Encounter/${this.encounterId}` } : undefined,
          valueBoolean: true,
          effectiveDateTime: new Date().toISOString()
        };
        return this.http.post(`${this.backendUrl}/Observation`, obs).toPromise();
      });

      await Promise.all(obsPromises);
      this.successToast.success('High-risk physical observations saved successfully.');
      this.dialog.closeAll();
    } catch (err) {
      console.error(err);
      this.errorService.openandCloseError('Failed to save high-risk observations.');
    } finally {
      this.isSaving = false;
    }
  }

  triggerMedicalEmergency() {
    const details = prompt('Enter short details for the medical emergency (e.g., location, immediate issue):');
    if (details === null) return; // User cancelled

    const message = details.trim() ? details.trim() : 'Immediate assistance required at triage.';

    this.unifiedNotificationService.broadcastEmergency(message, '🚨 MEDICAL EMERGENCY 🚨', '/app/triage');
    this.successToast.success('Emergency notification broadcasted to all clinical staff via Global Alert.');
  }

  triggerTopLevelEmergency() {
    const confirmCodeBlue = confirm('DECLARE A TOP-LEVEL EMERGENCY (CODE BLUE)? This will instantly alert ALL clinical staff across the hospital.');
    if (!confirmCodeBlue) return;
    
    this.unifiedNotificationService.broadcastEmergency(
      'An immediate unassigned emergency has arrived in Triage. All available personnel required!', 
      '🚨 CODE BLUE 🚨', 
      '/app/triage'
    );
    this.successToast.success('Code Blue broadcasted to all clinical staff.');
  }

  async markAsTriaged() {
    if (this.isSaving || !this.encounterId) return;

    const dialogRef = this.dialog.open(FinalizeTriageDialogComponent, {
      width: '450px',
      autoFocus: false,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result && result.priorityConcept) {
        this.isSaving = true;
        try {
          if (result.createEmergency && result.practitionerId) {
            this.unifiedNotificationService.broadcastEmergency(`Emergency Triage Assessed: ${result.priorityConcept?.text || 'Code Red'}`, '🚨 TRIAGE EMERGENCY 🚨', '/app/triage');
            await this.handleEmergencyAppointment(result.practitionerId, result.priorityConcept);
          } else {
            // Normal Triage Completion
            const encounter: any = await this.http.get(`${this.backendUrl}/Encounter/${this.encounterId}`).toPromise();
            encounter.status = 'triaged';
            encounter.priority = result.priorityConcept;
            await this.http.put(`${this.backendUrl}/Encounter/${this.encounterId}`, encounter).toPromise();
          }

          this.successToast.success('Triage completed successfully.');
          this.router.navigate(['/app/patient', this.patientId, 'encounters']);
        } catch (err) {
          console.error(err);
          this.errorService.openandCloseError('An error occurred while updating the encounter status.');
        } finally {
          this.isSaving = false;
        }
      }
    });
  }

  private async handleEmergencyAppointment(practitionerId: string, priorityConcept: any): Promise<void> {
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    
    const actor = encodeURIComponent(`Practitioner/${practitionerId}`);
    const startGe = encodeURIComponent(now.toISOString());
    const startLe = encodeURIComponent(`${dateStr}T23:59:59Z`);
    const status = encodeURIComponent('booked,arrived,pending,tentative,in-progress');
    
    const queries = `actor=${actor}&date=ge${startGe}&date=le${startLe}&status=${status}`;
    const url = `${this.backendUrl}/Appointment?${queries}`;
    const ctx = new HttpContext().set(LoadingUIEnabled, false);

    // Fetch existing appointments
    const existingAppointments: Appointment[] = await this.http.get<Bundle<any>>(url, { context: ctx }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= 50) return EMPTY;
        const nextLink = (bundle?.link ?? []).find((link) => String(link?.relation ?? '').toLowerCase() === 'next')?.url;
        const nextUrl = this.utilityService.normalizeNextLinkUrl(nextLink ?? null, url);
        return nextUrl ? this.http.get<Bundle<any>>(nextUrl, { context: ctx }) : EMPTY;
      }),
      map(bundle => (bundle?.entry ?? []).map(entry => entry.resource).filter(Boolean)),
      reduce((acc, curr) => acc.concat(curr), [] as any[]),
      map(resources => resources.filter((r: any) => r.resourceType === 'Appointment') as Appointment[]),
      catchError(() => of([]))
    ).toPromise() || [];

    // Construct Bundle
    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: []
    };

    // 1. Shift existing appointments forward by 30 mins
    const shiftMinutes = 30;
    const shiftMs = shiftMinutes * 60000;

    existingAppointments.forEach(appt => {
      if (appt.start && appt.end) {
        const shiftedAppt = JSON.parse(JSON.stringify(appt));
        shiftedAppt.start = new Date(new Date(appt.start).getTime() + shiftMs).toISOString();
        shiftedAppt.end = new Date(new Date(appt.end).getTime() + shiftMs).toISOString();
        
        bundle.entry!.push({
          resource: shiftedAppt,
          request: {
            method: 'PUT',
            url: `Appointment/${shiftedAppt.id}`
          }
        });
      }
    });

    // 2. Create the Emergency Appointment
    const emergencyEndMs = now.getTime() + shiftMs;
    const emergencyAppt: Appointment = {
      resourceType: 'Appointment',
      status: 'booked',
      start: now.toISOString(),
      end: new Date(emergencyEndMs).toISOString(),
      serviceCategory: [
        {
          coding: [
            {
              system: "https://elikita-server.daalitech.com/CodeSystem/appointment-service-category",
              code: "consultation",
              display: "Consultation"
            }
          ]
        }
      ],
      specialty: [
        {
          coding: [
            {
              system: "https://elikita-server.daalitech.com/CodeSystem/specialties",
              code: "outpatient-consultation",
              display: "Outpatient Consultation"
            }
          ]
        }
      ],
      participant: [
        {
          actor: { reference: `Patient/${this.patientId}` },
          status: 'accepted'
        },
        {
          actor: { reference: `Practitioner/${practitionerId}` },
          status: 'accepted'
        }
      ],
      extension: [
        {
          url: 'http://e-likita.org/fhir/StructureDefinition/triage-level',
          valueString: priorityConcept.text
        }
      ],
      description: `Emergency Triage Appointment - ${priorityConcept.text}`
    };

    bundle.entry!.push({
      resource: emergencyAppt,
      request: {
        method: 'POST',
        url: 'Appointment'
      }
    });

    // 3. Update Encounter to triaged
    const encounter: any = await this.http.get(`${this.backendUrl}/Encounter/${this.encounterId}`).toPromise();
    encounter.status = 'triaged';
    encounter.priority = priorityConcept;
    
    bundle.entry!.push({
      resource: encounter,
      request: {
        method: 'PUT',
        url: `Encounter/${this.encounterId}`
      }
    });

    // Execute Transaction
    await this.fhirResourceService.postBundle(bundle).toPromise();
  }
}
